import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { CircleAlert, UserIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@Hooks/usePermissions';
import UserRolePermissionForm from './userRolePermissionForm';
import service from '@Service';
import { PermissionsTablePagination } from './permissionsTablePagination';

export type PermissionRow = {
  id: string;
  name: string;
  description: string;
  requiresTarget: boolean;
  targetType: string | null;
  targetIds: {
    value: string;
    label: string;
  }[];
};

const UserRolePermissionList = ({
  activePermissions,
  userRoleId,
  fetchUserRole,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data: permissionsPayload, status } = usePermissions({
    searchParams: {
      include: 'userRolePermissions',
    },
  });

  const data: PermissionRow[] = useMemo(() => {
    const permissions = Array.isArray(permissionsPayload)
      ? permissionsPayload
      : (permissionsPayload?.data ?? []);

    const activeByPermissionId = activePermissions.reduce((acc, ap) => {
      (acc[ap.permissionId] ??= []).push(ap);
      return acc;
    }, {});

    return permissions.map((p) => {
      const matches = activeByPermissionId[p.id] ?? [];

      return {
        id: p.id,
        name: p.name,
        description: p.description,
        requiresTarget: p.requiresTargetedPermission,
        targetType: matches[0]?.targetType ?? null,
        targetIds:
          matches?.map((ap) => ({
            value: ap.targetId,
            label: null,
          })) ?? [],
      };
    });
  }, [permissionsPayload, activePermissions]);

  const total = data.length;

  const columns = useMemo<ColumnDef<PermissionRow>[]>(
    () => [
      {
        id: 'select',
        header: () => null,
        cell: ({ row }) => (
          <Checkbox
            disabled={!isEdit}
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
          />
        ),
        enableSorting: false,
        size: 48,
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'description',
        header: 'Scope',
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <UserRolePermissionForm edit isEdit={isEdit} data={row.original} />
        ),
        enableSorting: false,
      },
    ],
    [isEdit],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onSave = async () => {
    let error = '';
    const selected = table.getSelectedRowModel().rows.map((r) => r.original);

    for (const sp of selected) {
      if (sp.requiresTarget && (!sp.targetIds?.length || !sp.targetType)) {
        error = `Missing targeted permission config for ${sp.name}`;
        break;
      }
    }

    if (error) {
      toastError(`Error: ${error}`);
      return;
    }

    if (!selected.length) {
      toastError(`No adjustments made`);
      setIsEdit(!isEdit);
      return;
    }

    const permissionsWithTargets = selected.map((sp) => {
      const match = data.find((d) => d.id === sp.id);
      return { ...sp, targetIds: match?.targetIds ?? [] };
    });

    const res = await service.handleUserRolePermissions(
      userRoleId,
      permissionsWithTargets,
    );

    if (!res?.success) {
      toastError('Error updating user role');
    } else {
      toastSuccess('User Role Permissions updated successfully');
      setIsEdit(!isEdit);
      fetchUserRole();
    }
  };

  useEffect(() => {
    if (!activePermissions?.length) return;

    const initial: RowSelectionState = {};
    for (const p of activePermissions) {
      initial[p.permissionId] = true;
    }
    setRowSelection(initial);
  }, [activePermissions]);

  return (
    <div className="mt-2 space-y-4">
      <div className="flex h-16 items-center justify-between rounded-lg bg-zinc-100 p-4">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200">
            <UserIcon className="h-4 w-4 text-zinc-500" />
          </div>
          <div>
            Permissions Selected:{' '}
            {status === 'pending' ? (
              <Skeleton className="inline h-6 w-8" />
            ) : (
              <span className="font-bold">{activePermissions?.length}</span>
            )}
          </div>
        </div>

        <Button
          variant={isEdit ? 'primary' : 'outline'}
          onClick={() => {
            if (isEdit) {
              onSave();
            } else {
              setIsEdit(!isEdit);
            }
          }}
        >
          {isEdit ? 'Save Permissions' : 'Edit Permissions'}
        </Button>
      </div>

      <Input
        className="w-64"
        placeholder="Search Permissions..."
        value={globalFilter ?? ''}
        onChange={(e) => setGlobalFilter(e.target.value)}
      />

      {status === 'error' && (
        <Alert variant="destructive">
          <CircleAlert />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Error loading permissions.</AlertDescription>
        </Alert>
      )}

      <div className="overflow-hidden rounded-lg border border-zinc-200">
        <table className="w-full border-collapse">
          <thead className="bg-zinc-100">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="cursor-pointer border-b border-zinc-200 px-3 py-2 text-left text-sm font-medium text-zinc-600 select-none"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {{
                      asc: ' ↑',
                      desc: ' ↓',
                    }[header.column.getIsSorted() as string] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {status === 'pending'
              ? [...Array(pagination.pageSize)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={columns.length} className="p-3">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-zinc-200 hover:bg-zinc-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 text-sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <PermissionsTablePagination
        table={table}
        total={total}
        isLoading={status === 'pending'}
      />
    </div>
  );
};

export default UserRolePermissionList;
